import StencilHTMLElement from "./stencil-element";

class ProductOptionsPicker extends StencilHTMLElement {
  name = "ProductOptionsPicker";

  url;
  shouldUpdateUrl = true;
  hideInvalidOptionValues = false;
  sectionId;

  target;

  currentOptions;
  currentVariant; // now TRICKY to get! as we can't guarantee all variants output

  cache = new Map();
  renderToken = 0;

  constructor() {
    super();
  } 

  connectedCallback() {
    this.shouldUpdateUrl = (this.getAttribute('update-url') !== 'false');
    this.hideInvalidOptionValues = (this.getAttribute('hide-invalid-option-values') === 'true');
    this.forceSizeChoice = (this.getAttribute('force-size-choice') === 'true');
    this.url = this.getAttribute('url');
    this.sectionId = this.getAttribute('section-id');

    this.target = this.closest('product-details');

    // If the Size fieldset is hidden via hidden_options, the customer can't
    // pick — disable the force so the Add button isn't permanently locked.
    if (this.forceSizeChoice) {
      const sizeFieldset = this.querySelector('fieldset[data-is-size-option="true"]');
      if (sizeFieldset && sizeFieldset.closest('.col-12')?.classList.contains('d-none')) {
        this.forceSizeChoice = false;
      }
    }

    this.currentOptions = this.getCurrentOptions();
    this.currentVariant = this.getCurrentVariant();

    this.addEventListener('change', this.onOptionChange.bind(this));
    this.target.sellingPlanPicker?.addEventListener("change", this.onOptionChange.bind(this));

    // Handle variant change via non-standard option pickers
    this.proxyProductOptionPickers = this.target.querySelectorAll("[data-variant-option-picker]");
    this.proxyProductOptionPickers.forEach(element => {
      if(element.getAttribute("data-variant-option-picker") == "change"){
        element.addEventListener("change", this.onProxyOptionChange.bind(this));
      } else {
        element.addEventListener("click", this.onProxyOptionClick.bind(this));
      }
    });

    // Cache the initial content
    this.cache.set(this.buildUrl(this.currentOptions), this.target.innerHTML);

    // Watch for pointerover events and fetch / cache for quicker refreshes
    this.addEventListener("pointerover", this.onPreemptOptionChange.bind(this));

    this.updateAddButtonState();
  }

  async onPreemptOptionChange(e) {
    if(e.target.matches("[data-id]")){
      const presumptiveOption = { id: e.target.dataset.id, value: e.target.dataset.value, position: e.target.closest("fieldset").dataset.position };
      const presumptiveOptions = this.currentOptions.map(option =>
        option.position === presumptiveOption.position ? presumptiveOption : option
      );
      const presumptiveUrl = this.buildUrl(presumptiveOptions);
      await this.getHtml(presumptiveUrl);
    }
  }

  /**
   * Handle change event on a proxy option picker (e.g. the 'select' elements in
   * the sticky buy bar)
   * @param {Event} event 
   */
  onProxyOptionChange(event) {
    event.stopPropagation(); // Prevent the global P-O-P change event listener from also firing
    let optionName = event.target.options[event.target.selectedIndex].dataset.option;
    let optionValue = event.target.options[event.target.selectedIndex].dataset.value;
    console.log(this.logPrefix, "Proxy option changed", optionName, optionValue);
    this.clickOptionButton(optionName, optionValue);
  }

  /**
   * Handle click event on a proxy option picker (e.g. the colour swatches
   * container)
   * @param {Event} event 
   */
  onProxyOptionClick(event) {
    // TODO: Make sure the click was on an actual swatch, not just anywhere.
    this.clickOptionButton(event.target.dataset.option, event.target.dataset.value);
  }

  /**
   * Click the appropriate variant option radio button, if it exists
   * @param {String} name 
   * @param {String} value 
   */
  clickOptionButton(name, value) {
    let optionButton = this.querySelector(`[name*='${name}'][value='${value}']`);
    console.log(this.logPrefix, "Clicking option button", name, value, optionButton);
    if(optionButton){
      optionButton.click();
    } else {
      console.warn(this.logPrefix, `Tried to click option button, but couldn't find it using selector '[name*='${name}'][value='${value}']'`);
    }
  }

  /**
   * Update any proxy option selects when the variant changes. This isn't
   * required for elements with the [data-live-region="variant"] attribute
   */
  updateProxyOptionSelects() {
    this.proxyProductOptionPickers.forEach((element, outerIndex) => {
      let optionSelects = element.querySelectorAll("select");
      optionSelects.forEach((element, index) => {
        let optionIndex = parseInt(element.dataset.optionIndex) - 1;
        if(optionIndex == NaN){
          console.warn(this.logPrefix, "Tried to parse the proxy option select index, but failed. Have you set data-option-index on the select?");
        }

        // TODO: Defensive - check if element actually HAS the value at currentOptions[index]
        element.value = this.currentOptions[optionIndex].value;
        let event = document.createEvent('HTMLEvents');
        event.initEvent('change', false, true);
        element.dispatchEvent(event);
      });
    });
  }

  updateProxyOptionButtons() {
    // TODO!
    console.warn(this.logPrefix, "TODO: Need to update proxy option buttons!");
  }

  /**
   * Toggle the add-to-cart buttons (main + sticky) when force-size-choice is
   * active. Once a size is picked the main Add button sits inside a
   * [data-live-region="variant"] block — server-rendered Liquid already
   * handles sold-out / preorder / coming-soon / add-to-cart correctly there,
   * so we only intervene before a size is picked. The sticky Add lives
   * outside the live region, so we mirror the main button's state to it.
   */
  updateAddButtonState() {
    if (!this.forceSizeChoice) return;

    const sizeFieldset = this.querySelector('fieldset[data-is-size-option="true"]');
    if (!sizeFieldset) return;

    const checkedRadio  = sizeFieldset.querySelector('input[type="radio"]:checked');
    const checkedOption = sizeFieldset.querySelector('select option:checked:not([value=""])');
    const sizePicked    = !!(checkedRadio || checkedOption);

    const mainBtn   = this.target.querySelector(`#product-action-buttons-${this.sectionId} [name="add"]`);
    const stickyBtn = this.target.querySelector('#sticky-add');

    if (!sizePicked) {
      [mainBtn, stickyBtn].forEach(btn => {
        if (!btn) return;
        const span = btn.querySelector('span');
        btn.setAttribute('disabled', 'disabled');
        if (span) span.textContent = window.variantStrings.selectSize;
      });
      return;
    }

    if (mainBtn && stickyBtn) {
      const mainSpan = mainBtn.querySelector('span');
      const stickySpan = stickyBtn.querySelector('span');
      if (mainBtn.hasAttribute('disabled')) {
        stickyBtn.setAttribute('disabled', 'disabled');
      } else {
        stickyBtn.removeAttribute('disabled');
      }
      if (mainSpan && stickySpan) {
        stickySpan.textContent = mainSpan.textContent;
      }
    }
  }

  async onOptionChange(e) {
    const myToken = ++this.renderToken;
    this.currentOptions = this.getCurrentOptions();
    console.log(this.logPrefix, "Options changed", this.currentOptions);
    await this.render(myToken);
    if (myToken !== this.renderToken) {
      console.log(this.logPrefix, "Discarding stale onOptionChange", myToken, "current", this.renderToken);
      return;
    }
    this.currentVariant = this.getCurrentVariant();
    console.log(this.logPrefix, this.currentVariant);
    this.updateUrl(this.currentVariant?.id);
    this.updateSellingPlanInput(this.currentVariant?.id);
    this.updateVariantInputs(this.currentVariant?.id);
    this.updateAddButtonState();

    if(!this.currentVariant){
      // No variant matched the option values
      //this.toggleAvailability(false, window.variantStrings.unavailable);
      console.log(this.logPrefix, "No variant matched the selected options", this.currentOptions);

      if(this.hideInvalidOptionValues){
        /* 
         * We might have chosen an option that doesn't have a variant that 
         * aligns with the other options we previously chose. Try to find one
         * that does by looking for an option value from another option that
         * isn't hidden.
         */
        const changedOptionPosition = parseInt(e.target.closest('fieldset').dataset.position);
        const otherOptionPositions = [1,2,3].filter(position => position != changedOptionPosition);
        const possibleOptionValue = this.querySelector(`fieldset[data-position='${otherOptionPositions[0]}']`).querySelector('input[data-available="true"]');
        possibleOptionValue.click();
      }
    } else {
      // A variant matched the option values
      //this.toggleAvailability(true, window.variantStrings.addToCart);

      let event = new CustomEvent("variant:changed", { detail: {
        variant: this.currentVariant,
        quantity: this.target.querySelector("[name='quantity']")?.value || 1,
        target: this.target,
        product: this.target.product,
        selectedOption: e.target
      }});
      document.dispatchEvent(event);

      // Handle data attributes - [data-hide-when-sold-out], [data-show-when-sold-out]
      const isInStock = this.currentVariant.available;
      this.target.querySelectorAll("[data-hide-when-sold-out]").forEach(element => {
        element.classList.toggle(element.dataset.hideWhenSoldOut || "invisible", !isInStock);
      });
      this.target.querySelectorAll("[data-show-when-sold-out]").forEach(element => {
        element.classList.toggle(element.dataset.showWhenSoldOut || "invisible", isInStock);
      });
    }

    this.updateProxyOptionSelects();
    this.updateProxyOptionButtons();
  }

  /**
   * Get the variant object for the currently selected options
   * @returns {Object|null} The variant object for the currently selected 
   * options, or null if no variant matches the selected options.
   */
  getCurrentVariant() {
    const currentVariant = this.querySelector('[data-current-variant-json]')?.innerHTML;
    return !!currentVariant ? JSON.parse(currentVariant) : null;
  }

  /**
   * Get the currently selected options
   * @returns {Array<Object>} An array of objects representing the currently selected options
   */
  getCurrentOptions() {
    return Array.from(this.querySelectorAll('fieldset')).map(fieldset => {
      const checked = fieldset.querySelector('input:checked');
      if (!checked) console.warn(this.logPrefix, "No value checked in the fieldset?", fieldset);
      return { id: checked?.dataset.id, value: checked?.value, name: fieldset.dataset.name, position: fieldset.dataset.position };
    });
  }

  /**
   * Build the URL to fetch the updated section content from based on the
   * currently selected options and any other query parameters in the URL
   * @returns {String} The URL to fetch the updated section content from
   */
  buildUrl(options) {
    // Get existing params (if any, e.g. 'view', and update with variant ID)
    const urlSearchParams = new URLSearchParams(window.location.search);
    let params = Object.fromEntries(urlSearchParams.entries());

    // Remove the variant param - once we know if / what the variant is, we'll
    // add it back in
    delete params["variant"];

    // Use new query parameter, 'option_values', and let Shopify figure out the
    // variant based on the values.
    params["option_values"] = options.map(option => option.id).join(",");
    this.target.sellingPlanPicker ? (params["selling_plan"] = this.target.sellingPlanPicker.value) : delete params["selling_plan"];

    if(this.sectionId){
      params["section_id"] = this.sectionId;
    }

    const paramString = new URLSearchParams(params).toString();
    return `${this.url}?${paramString}`;
  }

  /**
   * Update the URL in the address bar to reflect the currently selected variant
   * @param {String} variantId 
   * @returns {void}
   */
  updateUrl(variantId) {
    if (!this.shouldUpdateUrl) return;
    if (!variantId) return;

    const urlSearchParams = new URLSearchParams(window.location.search);
    let params = Object.fromEntries(urlSearchParams.entries());

    // Now that we know the variant ID, use it instead of option values
    delete params["option_values"];
    params["variant"] = variantId;
    const paramString = new URLSearchParams(params).toString();
    window.history.replaceState({}, '', `${this.url}?${paramString}`);
  }

  /**
   * Fetch the updated section content and render it
   */
  async render(token) {
    const url = this.buildUrl(this.currentOptions);
    const updatedSectionHtml = await this.getHtml(url);
    if (token !== undefined && token !== this.renderToken) {
      console.log(this.logPrefix, "Discarding stale render", token, "current", this.renderToken);
      return;
    }
    const parsedSectionHtml = new DOMParser().parseFromString(updatedSectionHtml, 'text/html');

    // Remember which option control has focus so we can restore it after the
    // swap below — innerHTML replacement destroys the focused radio, dropping
    // keyboard focus to <body> mid-interaction (e.g. arrowing between sizes).
    // The radio's id prefix is NOT stable across a Section Rendering API
    // re-render, but its name ("{option}-{product.id}") and value are, so we
    // anchor to those.
    const activeBefore = document.activeElement;
    const focusRadio = (activeBefore && activeBefore.matches
      && activeBefore.matches('input[type="radio"]') && this.target.contains(activeBefore))
      ? { name: activeBefore.name, value: activeBefore.value }
      : null;

    this.target.querySelectorAll("[data-live-region='variant']").forEach(element => {
      // console.log(this.logPrefix, "Refreshing live region", element);
      if(element.id){
        const refreshedElement = parsedSectionHtml.getElementById(element.id);
        // console.log(this.logPrefix, "Refreshed element", refreshedElement);
        // console.log(this.logPrefix, "Prev element", element);
        if(refreshedElement){
          element.innerHTML = refreshedElement.innerHTML;
        } else {
          console.warn(this.logPrefix, "Tried to refresh live region, but response didn't have a matching ID");
          console.warn(this.logPrefix, "ID missing", element.id);
        }
      } else {
        console.warn(this.logPrefix, "Tried to refresh live region, but live element had no ID");
      }
    });

    // Restore keyboard focus to the same option control after the swap.
    if (focusRadio) {
      const toFocus = this.target.querySelector(
        `input[type="radio"][name="${CSS.escape(focusRadio.name)}"][value="${CSS.escape(focusRadio.value)}"]`
      );
      if (toFocus && document.activeElement !== toFocus) {
        toFocus.focus({ preventScroll: true });
      }
    }

    // Variant found matching those options.
    let variantRenderedEvent = new CustomEvent("variant:rendered", { detail: {
      variant: this.currentVariant,
      target: this.target,
      product: this.target.product
    }});
    document.dispatchEvent(variantRenderedEvent);

    // Announce the new variant state to screen readers — the price / stock /
    // button label above all changed silently via the live-region swap above.
    const v = this.currentVariant;
    window.MP.Utilities.announce(
      v ? v.title + ", " + (v.available ? "in stock" : "sold out") : "This combination is unavailable"
    );
  }

  async getHtml(url) {
    if (this.cache.has(url)) {
      console.log(this.logPrefix, "🎯 Cache hit! Returning cached", url); 
      return this.cache.get(url);
    } else {
      console.log(this.logPrefix, "🧨 Cache miss! Fetching fresh", url);
      const response = await fetch(url);
      const html = await response.text();
      this.cache.set(url, html);
      return html;
    }
  }

  /**
   * Update the visual state (e.g. button text, price visibility) when the
   * options selected don't match a variant (i.e. it's unavailable)
   */
  toggleAvailability(isAvailable, text) {
    const addButtons = this.target.querySelectorAll('[name="add"]');

    addButtons.forEach(addButton => {
      const addButtonText = addButton.querySelector('span');
      if(isAvailable){
        addButtonText.textContent = text;
        addButton.removeAttribute('disabled');
      } else {
        addButtonText.textContent = text;
        addButton.setAttribute('disabled', 'disabled');
      }
    });

    const elementsToHideOnUnavailable = this.target.querySelectorAll("[data-hide-when-unavailable]");
    elementsToHideOnUnavailable.forEach(element => {
      if(isAvailable) element.classList.remove(element.dataset.hideWhenUnavailable ? element.dataset.hideWhenUnavailable : "invisible");
      else element.classList.add(element.dataset.hideWhenUnavailable ? element.dataset.hideWhenUnavailable : "invisible");
    });
  }

  /**
   * Update the hidden input fields with the current variant ID, or clear them
   * if no variant matches current options
   */
  updateVariantInputs() {
    this.target.querySelectorAll(`#product-form-${this.sectionId}, #product-form-installment-${this.sectionId}`).forEach(productForm => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant?.id ?? '';
    });
  }

  /**
   * Update the selling plan input field with the current selling plan ID, or
   * the first valid one if the current variant doesn't support the current plan
   */
  updateSellingPlanInput() {
    // Need to check if this VARIANT supports the current selling plan.
    // If not, need to set it to a selling plan the variant DOES support
    if(this.target.sellingPlanPicker && this.currentVariant){
      if(!this.currentVariant.selling_plan_allocations.some(spa => {
        return spa.selling_plan_id == this.target.sellingPlanPicker.value;
      })){
        console.log(this.logPrefix, "New variant did not offer the same selling plan as the previous variant, so updating to one it does offer");
        this.target.sellingPlanPicker.value = this.currentVariant.selling_plan_allocations[0]?.selling_plan_id;
      }
    }
  }
}

export default ProductOptionsPicker;